import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, forkJoin } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { CrmAccount, CrmClient } from '../crm.model';
import { CrmService } from '../crm.service';
import { Customer } from '../../customer/customer.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { ToolbarService } from '../../services/toolbar.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import {
  CustomerPortalAccessStatus,
  PortalAccessRequest,
  PortalAccessView,
  PortalAdminRoleOption
} from './customer-portal-admin.model';
import { CustomerPortalAdminService } from './customer-portal-admin.service';
import { Permission } from '../../customer/customer-permission.model';
import { PartyService } from '../../party/party.service';

interface PermissionGroup {
  name: string;
  permissions: Permission[];
}

@Component({
  selector: 'app-customer-portal-access',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    ChipModule,
    ConfirmDialogModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    RippleModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './customer-portal-access.component.html',
  styleUrls: ['./customer-portal-access.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerPortalAccessComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly adminService = inject(CustomerPortalAdminService);
  private readonly crmService = inject(CrmService);
  private readonly partyService = inject(PartyService);
  private readonly toolbarService = inject(ToolbarService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly accesses = signal<PortalAccessView[]>([]);
  readonly customers = signal<Customer[]>([]);
  readonly clients = signal<CrmClient[]>([]);
  readonly accounts = signal<CrmAccount[]>([]);
  readonly roles = signal<PortalAdminRoleOption[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly accessDialog = signal(false);
  readonly roleDialog = signal(false);
  readonly submitted = signal(false);
  readonly roleSubmitted = signal(false);
  readonly lastUpdated = signal<Date | null>(null);
  readonly searchText = signal('');
  readonly crmClientFilter = signal<number | null>(null);
  readonly statusFilter = signal<CustomerPortalAccessStatus | null>(null);
  readonly selectedClientId = signal<number | null>(null);
  readonly accessRoleId = signal<number | null>(null);
  readonly roleSearchText = signal('');
  readonly selectedRoleId = signal<number | null>(null);
  readonly activeRoleId = signal<number | null>(null);
  readonly selectedAccess = signal<PortalAccessView | null>(null);

  readonly statusOptions = [
    { label: 'Invited', value: 'INVITED' as CustomerPortalAccessStatus },
    { label: 'Active', value: 'ACTIVE' as CustomerPortalAccessStatus },
    { label: 'Suspended', value: 'SUSPENDED' as CustomerPortalAccessStatus },
    { label: 'Revoked', value: 'REVOKED' as CustomerPortalAccessStatus }
  ];

  readonly accessForm = this.fb.group({
    customerId: [null as number | null, Validators.required],
    crmClientId: [null as number | null, Validators.required],
    crmAccountId: [null as number | null],
    status: ['INVITED' as CustomerPortalAccessStatus, Validators.required],
    roleId: [null as number | null, Validators.required]
  });

  readonly metrics = computed<PageMetric[]>(() => {
    const rows = this.accesses();
    const active = rows.filter(row => row.status === 'ACTIVE').length;
    const invited = rows.filter(row => row.status === 'INVITED').length;
    const suspended = rows.filter(row => row.status === 'SUSPENDED').length;
    const revoked = rows.filter(row => row.status === 'REVOKED').length;
    return [
      { label: 'Portal users', value: rows.length, hint: 'Tenant-scoped access records', tone: 'info' },
      { label: 'Active', value: active, hint: 'Live customer access', tone: active ? 'success' : 'warn' },
      { label: 'Invited', value: invited, hint: 'Awaiting activation', tone: invited ? 'warn' : 'info' },
      { label: 'Restricted', value: suspended + revoked, hint: `${suspended} suspended / ${revoked} revoked`, tone: suspended + revoked ? 'danger' : 'info' }
    ];
  });

  readonly customerOptions = computed(() => this.customers().map(customer => ({
    label: this.customerLabel(customer),
    value: customer.id
  })));

  readonly clientOptions = computed(() => this.clients().map(client => ({
    label: this.clientLabel(client),
    value: client.id
  })));

  readonly roleOptions = computed(() => this.roles().map(role => ({
    label: role.roleName,
    value: role.roleId
  })));

  readonly accessAccountOptions = computed(() =>
    this.accounts()
      .filter(account => !this.selectedClientId() || account.crmClientId === this.selectedClientId())
      .map(account => ({
        label: account.companyName,
        value: account.id
      }))
  );

  readonly filteredAccesses = computed(() => {
    const search = this.searchText().trim().toLowerCase();
    const clientId = this.crmClientFilter();
    const status = this.statusFilter();

    return this.accesses().filter(row => {
      if (clientId && row.crmClientId !== clientId) {
        return false;
      }
      if (status && row.status !== status) {
        return false;
      }
      if (!search) {
        return true;
      }

      const haystack = [
        row.customerName,
        row.emailAddress,
        row.crmClientName,
        row.crmAccountName,
        ...(row.roleNames || [])
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(search);
    });
  });

  readonly filteredDialogRoles = computed(() => {
    const search = this.roleSearchText().trim().toLowerCase();
    if (!search) {
      return this.roles();
    }

    return this.roles().filter(role =>
      role.roleName.toLowerCase().includes(search)
      || String(role.description || '').toLowerCase().includes(search)
      || (role.permissions || []).some(permission =>
        String(permission.permissionName || '').toLowerCase().includes(search)
        || String(permission.permissionCode || '').toLowerCase().includes(search)
      )
    );
  });

  readonly selectedInviteRole = computed(() =>
    this.roles().find(role => role.roleId === this.accessRoleId()) ?? null
  );

  readonly activeRole = computed(() => {
    const roleId = this.activeRoleId() ?? this.selectedRoleId();
    return this.roles().find(role => role.roleId === roleId) ?? null;
  });

  readonly activePermissionGroups = computed(() => this.groupPermissions(this.activeRole()));

  ngOnInit(): void {
    this.accessForm.get('crmClientId')?.valueChanges.subscribe(clientId => {
      const nextClientId = clientId ?? null;
      this.selectedClientId.set(nextClientId);
      const accountId = this.accessForm.get('crmAccountId')?.value;
      if (!accountId) {
        return;
      }

      const accountMatchesClient = this.accounts().some(account =>
        account.id === accountId && (!nextClientId || account.crmClientId === nextClientId)
      );
      if (!accountMatchesClient) {
        this.accessForm.patchValue({ crmAccountId: null }, { emitEvent: false });
      }
    });

    this.accessForm.get('roleId')?.valueChanges.subscribe(roleId => {
      this.accessRoleId.set(roleId ?? null);
    });

    this.loadReferenceData();
  }

  get activeSearchCount(): number {
    return [this.searchText(), this.crmClientFilter(), this.statusFilter()].filter(Boolean).length;
  }

  get lastUpdatedText(): string {
    return this.lastUpdated()
      ? `Updated ${this.lastUpdated()!.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : 'Ready for tenant-managed access changes';
  }

  loadReferenceData(): void {
    const companyId = this.companyId();
    if (!companyId) {
      return;
    }

    this.loading.set(true);
    forkJoin({
      accesses: this.adminService.listAccess(),
      customerParties: this.partyService.getParties(companyId, ['Customer'], '', 0, 500),
      clients: this.crmService.getClients(companyId),
      accounts: this.crmService.getAccounts(companyId),
      roles: this.adminService.getRoles()
    }).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: ({ accesses, customerParties, clients, accounts, roles }) => {
        const customers = (customerParties?.content || []).map(party => this.partyService.convertPartyToCustomer(party));
        this.accesses.set(this.sortAccesses(accesses || []));
        this.customers.set(this.sortByName(customers || [], customer => customer.name));
        this.clients.set(this.sortByName(clients || [], client => client.companyName));
        this.accounts.set(this.sortByName(accounts || [], account => account.companyName));
        this.roles.set(this.sortByName(roles || [], role => role.roleName));
        this.lastUpdated.set(new Date());
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Load Failed', detail: 'Could not load customer portal access data.' });
      }
    });
  }

  refreshAccesses(): void {
    this.adminService.listAccess().subscribe({
      next: rows => {
        this.accesses.set(this.sortAccesses(rows || []));
        this.lastUpdated.set(new Date());
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Refresh Failed', detail: 'Could not refresh customer portal access rows.' });
      }
    });
  }

  applySearch(): void {
    this.lastUpdated.set(new Date());
  }

  clearSearch(): void {
    this.searchText.set('');
    this.crmClientFilter.set(null);
    this.statusFilter.set(null);
  }

  openGrantAccess(): void {
    const defaultRoleId = this.roles()[0]?.roleId ?? null;
    this.accessForm.reset({
      customerId: null,
      crmClientId: this.crmClientFilter(),
      crmAccountId: null,
      status: 'INVITED',
      roleId: defaultRoleId
    });
    this.selectedClientId.set(this.crmClientFilter());
    this.accessRoleId.set(defaultRoleId);
    this.submitted.set(false);
    this.accessDialog.set(true);
  }

  hideAccessDialog(): void {
    this.accessDialog.set(false);
    this.submitted.set(false);
  }

  saveAccess(): void {
    if (this.saving()) {
      return;
    }
    this.submitted.set(true);
    if (this.accessForm.invalid) {
      this.accessForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Select a contact, CRM client, and portal role.' });
      return;
    }

    const raw = this.accessForm.getRawValue();
    const payload: PortalAccessRequest = {
      customerId: raw.customerId!,
      crmClientId: raw.crmClientId!,
      crmAccountId: raw.crmAccountId || null,
      status: raw.status!,
      roleIds: raw.roleId ? [raw.roleId] : []
    };

    this.saving.set(true);
    this.adminService.saveAccess(payload).pipe(finalize(() => this.saving.set(false))).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Customer portal access was updated.' });
        this.hideAccessDialog();
        this.refreshAccesses();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'Could not save portal access.' });
      }
    });
  }

  openRoleDialog(access: PortalAccessView): void {
    this.selectedAccess.set(access);
    const matchedRole = this.roles().find(role => (access.roleNames || []).includes(role.roleName)) ?? this.roles()[0] ?? null;
    this.roleSearchText.set('');
    this.selectedRoleId.set(matchedRole?.roleId ?? null);
    this.activeRoleId.set(matchedRole?.roleId ?? null);
    this.roleSubmitted.set(false);
    this.roleDialog.set(true);
  }

  hideRoleDialog(): void {
    this.roleDialog.set(false);
    this.roleSubmitted.set(false);
    this.roleSearchText.set('');
    this.selectedAccess.set(null);
  }

  selectRole(role: PortalAdminRoleOption): void {
    this.selectedRoleId.set(role.roleId);
    this.activeRoleId.set(role.roleId);
  }

  previewRole(role: PortalAdminRoleOption): void {
    this.activeRoleId.set(role.roleId);
  }

  saveRole(): void {
    if (this.saving()) {
      return;
    }
    this.roleSubmitted.set(true);
    const access = this.selectedAccess();
    const roleId = this.selectedRoleId();
    if (!access?.accessId || !roleId) {
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Select one portal role to continue.' });
      return;
    }

    this.saving.set(true);
    this.adminService.replaceRoles(access.accessId, [roleId]).pipe(finalize(() => this.saving.set(false))).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Role Updated', detail: 'Portal role assignment was updated.' });
        this.hideRoleDialog();
        this.refreshAccesses();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'Could not update the portal role.' });
      }
    });
  }

  confirmStatusChange(access: PortalAccessView, status: CustomerPortalAccessStatus): void {
    if (access.status === status) {
      return;
    }

    const actionLabel = status.charAt(0) + status.slice(1).toLowerCase();
    this.confirmationService.confirm({
      header: `${actionLabel} Portal Access`,
      message: `Set <b>${access.customerName || 'this contact'}</b> to <b>${actionLabel}</b>?`,
      icon: 'pi pi-exclamation-circle',
      acceptButtonStyleClass: status === 'REVOKED' ? 'p-button-danger p-button-text' : 'p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => this.applyStatusChange(access, status)
    });
  }

  statusSeverity(status: CustomerPortalAccessStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INVITED':
        return 'warn';
      case 'SUSPENDED':
        return 'danger';
      case 'REVOKED':
        return 'secondary';
      default:
        return 'info';
    }
  }

  primaryLifecycleDate(access: PortalAccessView): Date | string | null | undefined {
    return access.activatedAt || access.invitedAt || access.updatedAt;
  }

  lifecycleLabel(access: PortalAccessView): string {
    if (access.activatedAt) {
      return 'Activated';
    }
    if (access.invitedAt) {
      return 'Invited';
    }
    if (access.updatedAt) {
      return 'Updated';
    }
    return 'Pending';
  }

  rolePermissionCount(role: PortalAdminRoleOption | null): number {
    return Array.isArray(role?.permissions) ? role!.permissions.length : 0;
  }

  selectedInviteRoleName(): string {
    return this.selectedInviteRole()?.roleName || 'Select a role';
  }

  selectedInviteRoleDescription(): string {
    return this.selectedInviteRole()?.description || 'Portal-safe roles only.';
  }

  dialogScopeSummary(): string {
    const customer = this.customers().find(row => row.id === this.accessForm.get('customerId')?.value);
    const client = this.clients().find(row => row.id === this.accessForm.get('crmClientId')?.value);
    return [customer?.name, client?.companyName].filter(Boolean).join(' | ') || 'Map an existing contact to the right CRM scope.';
  }

  isRoleSelected(role: PortalAdminRoleOption): boolean {
    return this.selectedRoleId() === role.roleId;
  }

  private applyStatusChange(access: PortalAccessView, status: CustomerPortalAccessStatus): void {
    if (this.saving()) {
      return;
    }
    this.saving.set(true);
    this.adminService.updateStatus(access.accessId, status).pipe(finalize(() => this.saving.set(false))).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Status Updated', detail: `Portal access is now ${status.toLowerCase()}.` });
        this.refreshAccesses();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Update Failed', detail: 'Could not update portal access status.' });
      }
    });
  }

  private customerLabel(customer: Customer): string {
    const email = customer.emailAddress ? ` | ${customer.emailAddress}` : '';
    return `${customer.name}${email}`;
  }

  private clientLabel(client: CrmClient): string {
    return client.clientNumber ? `${client.clientNumber} - ${client.companyName}` : client.companyName;
  }

  private groupPermissions(role: PortalAdminRoleOption | null): PermissionGroup[] {
    const grouped = new Map<string, PermissionGroup>();
    for (const permission of role?.permissions || []) {
      const groupName = this.permissionGroupName(permission);
      if (!grouped.has(groupName)) {
        grouped.set(groupName, { name: groupName, permissions: [] });
      }
      grouped.get(groupName)?.permissions.push(permission);
    }

    return Array.from(grouped.values())
      .map(group => ({
        ...group,
        permissions: [...group.permissions].sort((left, right) => left.permissionName.localeCompare(right.permissionName))
      }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  private permissionGroupName(permission: Permission): string {
    if (permission.permissionName?.includes(':')) {
      return permission.permissionName.split(':')[0].trim();
    }
    return (permission.permissionCode || 'General')
      .split('.')[0]
      .replace(/-/g, ' ')
      .replace(/\b\w/g, letter => letter.toUpperCase());
  }

  private sortByName<T>(rows: T[], select: (row: T) => string | undefined | null): T[] {
    return [...rows].sort((left, right) => String(select(left) || '').localeCompare(String(select(right) || '')));
  }

  private sortAccesses(rows: PortalAccessView[]): PortalAccessView[] {
    return [...rows].sort((left, right) => {
      const leftTime = this.toEpoch(left.updatedAt || left.activatedAt || left.invitedAt);
      const rightTime = this.toEpoch(right.updatedAt || right.activatedAt || right.invitedAt);
      return rightTime - leftTime;
    });
  }

  private toEpoch(value: Date | string | null | undefined): number {
    if (!value) {
      return 0;
    }
    return new Date(value).getTime() || 0;
  }

  private companyId(): number | null {
    return this.toolbarService.customer()?.company?.id
      ?? this.sessionStorageService.getCompany()?.id
      ?? null;
  }
}
